/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import { Suspense, useEffect, useMemo, useState } from 'react';

import { lazy } from '@loadable/component';
import { useSelector } from 'react-redux';
import MediaQuery from 'react-responsive';
import { Redirect } from 'react-router';
import styled from 'styled-components';

import { CenterLoader } from 'components/loader';
import { DynamicLoader } from 'components/markets';
import Render from 'components/render';
import Seo from 'components/seo';
import { INDIAN_EXCHANGE_CONTRACT_TYPES, IS_INDIAN_EXCHANGE } from 'constants/constants';
import { STORAGE_KEYS } from 'constants/enums';
import routes from 'constants/routes';
import { isFutures, isOptions, isSpotContract } from 'helpers/utils';
import useLocalStorage from 'hooks/useLocalStorage';
import i18n from 'i18n/config';
import { basketOrderSwitchSelector } from 'selectors/basketOrdersSelectors';
import {
  lastTradePriceMovementSelector,
  lastTradePriceSelector,
} from 'selectors/priceSelectors';
import { ContractType } from 'types/IProducts';

import { device } from '../../helpers/screenSizes';
import OptionsCombosSlides from './OptionsCombos/Slides';
import TradeMobile from './trade_mobile';

const TradeContainer = styled.div`
  @media ${device.down.md} {
    padding-bottom: inherit;
  }

  @media ${device.down.sm} {
    .placeorder-container-wrapper {
      padding: 0px !important;
    }
  }

  .placeorder-container {
    background: var(--main-bg-surface-alt) !important;
  }

  .chart-container {
    height: 100%;
    z-index: auto;
    padding-right: 0px;
    // outline: 1px solid ${props => props.theme.separator};

    @media ${device.down.xxs} {
      height: 400px;
    }
  }

  .trade-container .right-container {
    padding-left: 48px;
    padding-right: 0px;
  }

  &.trade-container {
    width: 100%;
    margin-bottom: 30px;
    @media ${device.down.md} {
      margin-bottom: 0;
    }
  }

  .align-items-center {
    align-items: center;
  }

  .obrt-container {
    @media ${device.down.md} {
      padding: 0px;
    }
  }
  &.options-chain-trade-container {
    @media ${device.down.md} {
      overflow: hidden;
    }
  }
`;

const OptionsChain = lazy(() =>
  import(/* webpackChunkName: "OptionsChain" */ 'containers/options_chain')
);

const TradeDesktop = lazy(() =>
  import(/* webpackChunkName: "TradeDesktop" */ './trade_desktop')
);

const ContractExpiry = lazy(() =>
  import(
    /* webpackChunkName: "ContractExpiry" */ /* webpackPrefetch: true */ 'components/contractExpiry'
  )
);

const getMetaDesc = product => {
  const {
    contract_type,
    short_description,
    description,
    underlying_asset,
    contract_unit_currency,
    initial_margin,
  } = product || {};
  const leverage = Math.trunc(100 / Number(initial_margin));
  const type = short_description?.toLowerCase().includes('call')
    ? i18n.t('trading:optionContractType.call')
    : i18n.t('trading:optionContractType.put');
  const name = underlying_asset?.name || '';

  if (IS_INDIAN_EXCHANGE) {
    switch (contract_type) {
      case 'call_options':
      case 'put_options':
        return i18n.t('trading:metaDescription.indian.callOptions', {
          name,
          type,
          leverage,
        });
      default:
        return i18n.t('trading:metaDescription.indian.default', {
          name,
          type,
          leverage,
        });
    }
  }

  switch (contract_type) {
    case 'call_options':
    case 'put_options':
      return i18n.t('trading:metaDescription.global.callOptions', {
        name,
        type,
        leverage,
      });
    case 'move_options':
    case 'options_combos':
      return i18n.t('trading:metaDescription.global.optionSpreads', {
        name,
        type,
        leverage,
      });
    case 'futures':
    case 'perpetual_futures':
      return i18n.t('trading:metaDescription.global.futures', {
        name,
        leverage,
      });
    case 'spot':
      return i18n.t('trading:metaDescription.global.spot', {
        currency: contract_unit_currency ?? '',
      });
    default:
      return i18n.t('trading:metaDescription.global.default', {
        description: short_description || description,
      });
  }
};

const getContractName = contract_type => {
  switch (contract_type) {
    case 'futures':
      return `futures trading`;
    case 'perpetual_futures':
      return `Perpetual Swaps`;
    case 'interest_rate_swaps':
      return `interest rate swap trading`;
    case 'spreads':
      return `Spreads`;
    case 'spot':
      return `Spot Market`;
    case 'call_options':
      return `Options Chain`;
    case 'put_options':
      return `Options Chain`;
    case 'move_options':
      return 'Straddle Contracts';
    case 'tracker':
      return `Tracker`;
    default:
      return '';
  }
};

const Trade = props => {
  const { isLoggedIn, selected_product, themeMode, stateContractType } = props;

  if (
    selected_product &&
    INDIAN_EXCHANGE_CONTRACT_TYPES.indexOf(selected_product?.contract_type) === -1
  ) {
    // window.location.href = '/markets';
    <Redirect to={routes.futures.markets} />;
  }
  const [isOptionsCombosSlideModalOpen, setOptionsCombosSlideModalOpen] = useState(false);
  const [isOptionSlidesShown, setIsOptionsSlidesShown] = useLocalStorage(
    STORAGE_KEYS.OPTION_COMBOS_SLIDES,
    false
  );
  // usePaletteTags('trade');

  const lastTradePrice = useSelector(lastTradePriceSelector);
  const lastTradePriceMovement = useSelector(lastTradePriceMovementSelector);
  const isBasketView = useSelector(state => basketOrderSwitchSelector(state));

  const isProductExpired = selected_product && selected_product.state === 'expired';

  const pageTitle = useMemo(() => {
    const {
      short_description,
      contract_type,
      underlying_asset,
      spot_index,
      contract_unit_currency,
    } = selected_product || {};
    const arrow = lastTradePriceMovement === 'up' ? '▲' : '▼';
    const priceInfo = lastTradePrice
      ? `${arrow} ${lastTradePrice} ${
          spot_index?.config?.quoting_asset ?? contract_unit_currency
        }`
      : '';
    const exchangeSuffix = !IS_INDIAN_EXCHANGE ? ' Global' : '';

    const getTitleByContractType = () => {
      const baseTitle = `Trade ${
        isSpotContract(contract_type)
          ? contract_unit_currency
          : underlying_asset?.name || ''
      } ${getContractName(contract_type)}`;

      switch (true) {
        case contract_type === ContractType.MoveOptions:
          return `Trade ${underlying_asset?.name} ${getContractName(contract_type)}`;

        case isOptions(contract_type):
          return `Trade ${underlying_asset?.name || ''} ${
            short_description?.toLowerCase().includes('call') ? 'Call' : 'Put'
          } ${getContractName(contract_type)}`;

        case isFutures(contract_type):
          return `${priceInfo} | ${baseTitle}`;

        case isSpotContract(contract_type):
          return `${priceInfo} | ${baseTitle}`;

        case contract_type === ContractType.Tracker:
          return `${priceInfo} | ${baseTitle}`;

        default:
          return baseTitle;
      }
    };
    return `${getTitleByContractType()} - Delta Exchange${exchangeSuffix}`;
  }, [selected_product, lastTradePrice, lastTradePriceMovement]);

  useEffect(() => {
    if (selected_product?.contract_type === ContractType.OptionsCombos) {
      if (isOptionSlidesShown) return;
      setIsOptionsSlidesShown(true);
      setOptionsCombosSlideModalOpen(true);
    }
  }, [selected_product, isOptionSlidesShown, setIsOptionsSlidesShown]);

  const optionsCombosSlidesModalClose = () => {
    setOptionsCombosSlideModalOpen(false);
  };

  return (
    <>
      <Seo
        title={pageTitle}
        description={getMetaDesc(selected_product)}
        keywords="trading engine, Delta Exchange, DETO, low fees, high liquidity, perpetual swaps, trade, options, spreads, crypto derivatives exchange, futures, BTC, Cryptocurrency trading, Crypto trading, best cryptocurrency exchange, Best cryptocurrency trading platform."
      />
      <Suspense fallback={<CenterLoader />}>
        <TradeContainer
          className={`trade-container ${
            stateContractType === 'options_chain' ? 'options-chain-trade-container' : ''
          }`}
        >
          <Render when={isProductExpired}>
            <ContractExpiry contract_type={stateContractType} />
          </Render>
          <MediaQuery query={device.up.md}>
            <TradeDesktop
              selected_product={selected_product}
              themeMode={themeMode}
              isLoggedIn={isLoggedIn}
              stateContractType={stateContractType}
              isBasketView={isBasketView}
            />
          </MediaQuery>
          <Render when={isOptionsCombosSlideModalOpen}>
            <OptionsCombosSlides
              isModalOpen={isOptionsCombosSlideModalOpen}
              handleModalClose={optionsCombosSlidesModalClose}
            />
          </Render>
        </TradeContainer>
      </Suspense>
      <MediaQuery query={device.down.md}>
        <Suspense fallback={<DynamicLoader />}>
          <Render when={stateContractType !== 'options_chain'}>
            <TradeMobile />
          </Render>
          <Render when={stateContractType === 'options_chain'}>
            <OptionsChain />
          </Render>
        </Suspense>
      </MediaQuery>
    </>
  );
};

export default Trade;
